import React from "react";
import { Page, View, Text, Image } from "@react-pdf/renderer";
import { styles, mark } from "./pdfTheme";

/*
 * The cover frame, shared with HSE and CSA. The artwork carries no wording,
 * so one file serves all of them; the product name is drawn over it.
 */
import coverArt from "../../../HSE_Report_Cover.png";

/* The wordmark, for the running head. */
import logoArt from "../../../momentum_logo.png";

/**
 * The artwork, as a source react-pdf will actually load.
 *
 * Webpack turns the import into a root-relative path — /static/media/….png —
 * and react-pdf refuses it ("Only absolute URLs are supported"), logged as a
 * warning and then skipped. Resolved against the page's own URL instead, so
 * it works under a dev server and under a deployed build at any path.
 */
export function absoluteSrc(art, href) {
  if (!art) return art;
  if (/^(https?:|data:|blob:|file:)/i.test(art)) return art;
  if (!href) return art;
  try {
    return new URL(art, href).href;
  } catch (e) {
    return art;
  }
}

const logoSrc = absoluteSrc(
  logoArt,
  typeof window !== "undefined" && window.location ? window.location.href : null
);

const coverSrc = absoluteSrc(
  coverArt,
  typeof window !== "undefined" && window.location ? window.location.href : null
);

/**
 * The parts of the document both PDF layouts share. Ported from
 * Csa/report/pdfSections.js — Standard and Elaborate differ only in the
 * findings section; cover, narrative, score analysis, overall assessment and
 * scope are identical, so they live here and are imported by both.
 */

/* ------------------------------------------------------------------ */
/* formatting                                                          */
/* ------------------------------------------------------------------ */

export const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (isNaN(x.getTime())) return String(d);
  return x.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

export const dateRange = (a, b) => {
  if (!a && !b) return "—";
  if (a && b && a !== b) return `${fmtDate(a)} – ${fmtDate(b)}`;
  return fmtDate(a || b);
};

export const pctText = (n) => (n === null || n === undefined ? "Not assessed" : `${n}%`);

/** The line that tells two documents apart. Empty for a preview. */
export function issueLine(header) {
  const i = header.issue;
  if (!i) return "";
  return [
    `Issue ${i.no}`,
    fmtDate(i.issued_at),
    `${i.assessed_count} assessed`,
    `${i.selected_count} reported`,
  ].join("  ·  ");
}

/* ------------------------------------------------------------------ */
/* chrome                                                              */
/* ------------------------------------------------------------------ */

/** Marks every page of a preview — the condition `issueLine` uses too. */
export function Watermark({ header }) {
  if (header && header.issue) return null;
  return (
    <View style={mark.wrap} fixed>
      <Text style={mark.text}>PREVIEW</Text>
      <Text style={mark.sub}>NOT FOR ISSUE</Text>
    </View>
  );
}

export function Chrome({ header }) {
  return (
    <>
      {/* <Watermark header={header} /> */}
      <View style={styles.runningHead} fixed>
        <View style={styles.runningHeadLeft}>
          <Image src={logoSrc} style={styles.runningLogo} />
          <Text>
            {header.organization} — {header.site}
          </Text>
        </View>
        <Text>Electrical Audit</Text>
      </View>
      <View style={styles.runningFoot} fixed>
        <Text>{header.report_id}</Text>
        <Text>{issueLine(header)}</Text>
        <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
      </View>
    </>
  );
}

export function Heading({ no, title }) {
  return (
    <View>
      <Text style={styles.h1}>
        {no}. {title}
      </Text>
      <View style={styles.h1Rule} />
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* cover                                                               */
/* ------------------------------------------------------------------ */

/**
 * The cover, in the same frame HSE and CSA use. The artwork carries no
 * wording; which report it is comes from the title drawn over it.
 */
export function Cover({ header, summary }) {
  return (
    <Page size="A4" style={[styles.page, styles.coverPage]}>
      <View style={styles.coverBackdrop}>
        <Image src={coverSrc} style={styles.coverImage} />
      </View>

      <View style={styles.coverPanel}>
        <View style={styles.coverPanelInner}>
          <Row label="Client:" value={header.organization} />
          <Row label="Location:" value={header.site} />
          <Row label="Service:" value="Electrical Audit" />
          <Row label="Date:" value={dateRange(header.start_date, header.end_date)} />
        </View>
      </View>

      <View style={styles.coverProduct}>
        <Text style={styles.coverProductText}>Electrical</Text>
        <Text style={styles.coverProductText}>Audit</Text>
        <View style={styles.coverYearRow}>
          <View style={styles.coverYearLine} />
          <Text style={styles.coverYear}>{yearOf(header.end_date)}</Text>
        </View>
      </View>

      {/* <Watermark header={header} /> */}
    </Page>
  );
}

function Row({ label, value }) {
  return (
    <View style={styles.coverDetailRow}>
      <Text style={styles.coverDetailLabel}>{label}</Text>
      <Text style={styles.coverDetailValue}>{value}</Text>
    </View>
  );
}

export function CoverDetails({ header, summary }) {
  return (
    <View>
      <View style={styles.coverRule} />
      <Text style={styles.detailsHead}>{header.organization}</Text>
      <Text style={styles.detailsSite}>{header.site}</Text>

      <View style={styles.coverGrid}>
        <Cell label="ASSESSMENT DATES" value={dateRange(header.start_date, header.end_date)} />
        <Cell
          label="OVERALL CONFORMANCE"
          value={
            pctText(summary.overall_pct) +
            (summary.maturity_band ? " — " + summary.maturity_band : "")
          }
        />
        <Cell label="PREPARED BY" value={header.prepared_by || "—"} />
        <Cell
          label="REVIEWED BY"
          value={header.was_reviewed ? header.reviewed_by || "Reviewed" : "Not reviewed"}
        />
        <Cell label="FRAMEWORK VERSION" value={header.framework_version} />
        <Cell label="REPORT REFERENCE" value={header.report_id} />
      </View>
    </View>
  );
}

function Cell({ label, value }) {
  return (
    <View style={styles.coverCell}>
      <Text style={styles.coverLabel}>{label}</Text>
      <Text style={styles.coverValue}>{value}</Text>
    </View>
  );
}

/** The year the assessment finished, not today's — a reprint must not relabel itself. */
function yearOf(d) {
  const y = String(d || "").slice(0, 4);
  return /^[0-9]{4}$/.test(y) ? y : String(new Date().getFullYear());
}

/* ------------------------------------------------------------------ */
/* narrative                                                           */
/* ------------------------------------------------------------------ */

/** Auditor prose, split into paragraphs on blank lines — there is no rich-text editor behind this. */
export function Narrative({ section }) {
  const paras = String(section.body || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <View>
      <Heading no={section.no} title={section.title} />
      {paras.length ? (
        paras.map((p, i) => (
          <Text key={i} style={styles.body}>
            {p}
          </Text>
        ))
      ) : (
        <Text style={styles.empty}>This section has not been written.</Text>
      )}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* score analysis                                                      */
/* ------------------------------------------------------------------ */

export function ScoreAnalysis({ section }) {
  const d = section.data;

  return (
    <View>
      <Heading no={section.no} title={section.title} />

      <View style={styles.scoreHero}>
        <Text style={styles.scoreBig}>{pctText(d.overall.pct)}</Text>
        <View>
          <Text style={styles.scoreBand}>{d.overall.band}</Text>
          <Text style={styles.muted}>
            {d.overall.assessed} of {d.overall.total} checkpoints assessed
            {d.overall.not_assessed ? ` · ${d.overall.not_assessed} out of scope` : ""}
          </Text>
        </View>
      </View>

      <Text style={styles.h2}>By area</Text>
      <View>
        <View style={[styles.tRow, styles.tHead]}>
          <Text style={[styles.tHeadCell, { width: "5%" }]}>#</Text>
          <Text style={[styles.tHeadCell, { width: "50%" }]}>AREA</Text>
          <Text style={[styles.tHeadCell, { width: "14%" }]}>ASSESSED</Text>
          <Text style={[styles.tHeadCell, { width: "14%" }]}>SCORE</Text>
          <Text style={[styles.tHeadCell, { width: "14%" }]}>BAND</Text>
        </View>
        {d.areas.map((a, i) => (
          <View key={a.no} style={[styles.tRow, i % 2 ? styles.zebra : {}]} wrap={false}>
            <Text style={[styles.tCell, { width: "8%" }]}>{a.no}</Text>
            <Text style={[styles.tCell, { width: "50%" }]}>{a.name}</Text>
            <Text style={[styles.tCell, { width: "14%" }]}>
              {a.assessed}/{a.total}
            </Text>
            <Text style={[styles.tCell, { width: "14%", fontWeight: 700 }]}>{pctText(a.pct)}</Text>
            <Text style={[styles.tCell, { width: "14%" }]}>{a.band}</Text>
          </View>
        ))}
      </View>

      <Text style={styles.h2} break>
        By category
      </Text>
      {d.areas.map((a) => (
        <View key={a.no} wrap={false}>
          <Text style={styles.h3}>
            {a.no}. {a.name}
          </Text>
          {a.categories.map((c, i) => (
            <View key={c.name} style={[styles.tRow, i % 2 ? styles.zebra : {}]}>
              <Text style={[styles.tCellTight, { width: "58%" }]}>{c.name}</Text>
              <Text style={[styles.tCellTight, { width: "14%" }]}>
                {c.assessed}/{c.total}
              </Text>
              <Text style={[styles.tCellTight, { width: "14%", fontWeight: 700 }]}>
                {pctText(c.pct)}
              </Text>
              <Text style={[styles.tCellTight, { width: "14%" }]}>{c.band}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* overall assessment                                                  */
/* ------------------------------------------------------------------ */

export function OverallAssessment({ section }) {
  const d = section.data;
  return (
    <View>
      <Heading no={section.no} title={section.title} />
      <View style={styles.scoreHero}>
        <Text style={styles.scoreBig}>{pctText(d.pct)}</Text>
        <Text style={styles.scoreBand}>{d.band}</Text>
      </View>
      <Text style={styles.body}>{d.statement}</Text>

      <View style={[styles.tRow, styles.tHead, { marginTop: 10 }]}>
        <Text style={[styles.tHeadCell, { width: "25%" }]}>CONFORMANT</Text>
        <Text style={[styles.tHeadCell, { width: "25%" }]}>PARTIAL</Text>
        <Text style={[styles.tHeadCell, { width: "25%" }]}>NON-CONFORMANT</Text>
        <Text style={[styles.tHeadCell, { width: "25%" }]}>NOT APPLICABLE</Text>
      </View>
      <View style={styles.tRow}>
        <Text style={[styles.tCell, { width: "25%" }]}>{d.counts.conformant}</Text>
        <Text style={[styles.tCell, { width: "25%" }]}>{d.counts.partial}</Text>
        <Text style={[styles.tCell, { width: "25%" }]}>{d.counts.non_conformant}</Text>
        <Text style={[styles.tCell, { width: "25%" }]}>{d.counts.not_applicable}</Text>
      </View>
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* scope                                                               */
/* ------------------------------------------------------------------ */

export function Scope({ section }) {
  const d = section.data;
  return (
    <View>
      <Heading no={section.no} title={section.title} />
      <Text style={styles.body}>{d.note}</Text>
      <Text style={styles.muted}>
        {d.assessed} of {d.total} checkpoints were assessed
        {d.excluded ? `; ${d.excluded} were excluded.` : "."}
      </Text>

      {d.exclusions.length ? (
        <View style={{ marginTop: 10 }}>
          {d.exclusions.map((e, i) => (
            <View key={i} style={styles.scopeItem} wrap={false}>
              <Text style={styles.scopeName}>{e.name}</Text>
              <Text style={styles.muted}>{e.reason}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* scoring table                                                       */
/* ------------------------------------------------------------------ */

/**
 * The same table as the Finish tab's ElectricalScoringTable - a fixed set
 * of parameters scored by hand, not derived from checkpoint answers. Only
 * ever drawn once `scoring_snapshot` exists (ElectricalReport.js leaves
 * this section out entirely for a draft audit, before one has been saved).
 */
export function ScoreTable({ section }) {
  const d = section.data;
  return (
    <View wrap={false}>
      <Heading no={section.no} title={section.title} />
      <View>
        <View style={[styles.tRow, styles.tHead]}>
          <Text style={[styles.tHeadCell, { width: "60%" }]}>PARAMETER</Text>
          <Text style={[styles.tHeadCell, { width: "20%" }]}>MAX SCORE</Text>
          <Text style={[styles.tHeadCell, { width: "20%" }]}>SCORE OBTAINED</Text>
        </View>
        {d.parameters.map((p, i) => (
          <View key={p.parameter} style={[styles.tRow, i % 2 ? styles.zebra : {}]} wrap={false}>
            <Text style={[styles.tCell, { width: "60%" }]}>{p.parameter}</Text>
            <Text style={[styles.tCell, { width: "20%" }]}>{p.max_score}</Text>
            <Text style={[styles.tCell, { width: "20%" }]}>{p.score_obtained}</Text>
          </View>
        ))}
        <View style={[styles.tRow, styles.tHead]} wrap={false}>
          <Text style={[styles.tHeadCell, { width: "60%" }]}>CUMULATIVE</Text>
          <Text style={[styles.tHeadCell, { width: "20%" }]}>{d.max_total}</Text>
          <Text style={[styles.tHeadCell, { width: "20%" }]}>{d.score_obtained_total}</Text>
        </View>
      </View>
    </View>
  );
}
